/**
 * Small Experience Card Component
 * Compact card for displaying Nexgo experiences in chat/discover screen
 */

import React, { useRef } from 'react';
import {
  spacing,
  borders,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { ExperienceCard as ExperienceCardType } from '@/constants/chatScreenData';
import { ExperienceTypeBadge } from '@/components/ui/experience-type-badge';
import type { Swiper as SwiperType } from 'swiper';
import { ExperienceCardImageSwiper } from './ExperienceCardImageSwiper';
import { useCardImagePreloader } from '@/hooks/useImagePreloader';

export interface SmallExperienceCardProps {
  experience: ExperienceCardType & {
    images?: string[]; // Array of all images for carousel
  };
  onClick?: () => void;
}

export const SmallExperienceCard: React.FC<SmallExperienceCardProps> = ({
  experience,
  onClick,
}) => {
  const [isSwiping, setIsSwiping] = React.useState(false);
  const swiperRef = React.useRef<SwiperType | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Get all images for carousel (use images array if available, otherwise fallback to single image)
  // Normalize images to ensure they're always string arrays
  const normalizeImages = (imgArray: unknown): string[] => {
    if (!imgArray) return [];
    if (Array.isArray(imgArray)) {
      return imgArray.filter(
        (item): item is string => typeof item === 'string' && item.length > 0
      );
    }
    if (typeof imgArray === 'string' && imgArray.length > 0) {
      return [imgArray];
    }
    return [];
  };

  const imagesArray = experience ? normalizeImages(experience.images) : [];
  const image = experience?.image || '';
  const images = imagesArray.length > 0 ? imagesArray : image ? [image] : [];

  // Preload images when card enters viewport (must be called before any early returns)
  useCardImagePreloader(cardRef, images, {
    rootMargin: '200px', // Start preloading when card is 200px away
    maxConcurrent: 3, // Preload up to 3 images at a time
  });

  // Guard against missing experience data
  if (!experience) {
    return null;
  }

  const title = experience.title || '';
  const location = experience.location || '';
  const type = experience.type || 'activities';

  // Handle Swiper touch events to detect swiping
  const handleTouchStart = () => {
    setIsSwiping(false);
  };

  const handleTouchMove = () => {
    setIsSwiping(true);
  };

  const handleTouchEnd = () => {
    // Reset after a short delay to allow click handlers to check the flag
    setTimeout(() => setIsSwiping(false), 150);
  };

  // Handle hover to start/stop autoplay
  const handleMouseEnter = () => {
    if (swiperRef.current && images.length > 1) {
      // Immediately slide to next image for instant feedback
      swiperRef.current.slideNext();
      swiperRef.current.autoplay?.start();
    }
  };

  const handleMouseLeave = () => {
    if (swiperRef.current) {
      swiperRef.current.autoplay?.stop();
    }
  };

  // Handle card click - prevent if user was swiping
  const handleCardClick = (e: React.MouseEvent) => {
    if (isSwiping) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.();
  };

  return (
    <div
      ref={cardRef}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        width: '180px',
        height: '160px',
        borderRadius: borders.radius.lg,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      {/* Image Container */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#E7E5E4', // Fallback background color
          borderRadius: borders.radius.lg,
          overflow: 'hidden',
        }}
      >
        <ExperienceCardImageSwiper
          images={images}
          title={title}
          swiperRef={swiperRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          sizes='(max-width: 768px) 100vw, 180px'
          className='small-experience-card-swiper'
        />
      </div>

      {/* Gradient Overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 55.769%, rgba(0,0,0,0.8) 100%)',
          zIndex: 1,
        }}
      />

      {/* Content Container */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          flex: 1,
          paddingLeft: spacing[2],
          paddingRight: spacing[2],
          paddingTop: spacing[2],
          paddingBottom: spacing[2],
          minHeight: 0,
        }}
      >
        {/* Badge at top right */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[2],
            alignItems: 'flex-end',
            width: '100%',
          }}
        >
          <ExperienceTypeBadge type={type} size='small' />
        </div>

        {/* Title and Location at bottom */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[0.5],
            width: '100%',
            color: '#E7E5E4',
          }}
        >
          <p
            style={{
              fontFamily: fontPrimary,
              fontSize: '14px',
              fontWeight: typography.weight.semibold,
              color: '#E7E5E4',
              lineHeight: '1.4',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              margin: 0,
              whiteSpace: 'pre-wrap',
              minHeight: '39.2px', // 14px * 1.4 * 2 lines = 39.2px
            }}
          >
            {title}
          </p>
          {location && (
            <p
              style={{
                fontFamily: fontPrimary,
                fontSize: '11px',
                fontWeight: typography.weight.light,
                color: '#E7E5E4',
                lineHeight: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                margin: 0,
              }}
            >
              {location}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
